import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { GroupedAccount } from '@epicuro-next/ataraxia/models';
import { Contact } from '@epicuro-next/models/contacts';
import { detectBrowser } from '@epicuro-next/platform/browser-detection';
import { Account } from 'graphql-api/gql-api-interfaces';
import { combineLatest, map, Observable, of, startWith, switchMap } from 'rxjs';

@Component({
  selector: 'atx-searchable-contacts-list',
  templateUrl: './searchable-contacts-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SearchableContactsListComponent implements OnInit, AfterViewInit {
  @ContentChild('contactsList', { static: false })
  protected contactsListTemplateRef?: TemplateRef<unknown>;
  @ViewChild('searchRef')
  protected searchRef!: ElementRef<HTMLInputElement>;

  @Input() public contacts$!: Observable<Contact[]>;
  @Input() public isSalesRep = false;
  @Input() public activeAccount$!: Observable<Account | null>;
  @Input() public hyperlinkEmail = false;

  protected contactsFilteredBySearch$!: Observable<Contact[]>;
  protected groupedAccounts$: Observable<GroupedAccount[]> = of([]);
  protected searchInputControl = new UntypedFormControl();
  private isDesktop = !(detectBrowser('isAndroid') || detectBrowser('isIos'));

  public ngOnInit(): void {
    this.subscribeToContactsState();
  }

  public ngAfterViewInit() {
    if (this.isDesktop) {
      this.searchRef.nativeElement.focus();
    }
  }

  protected trackByAccountId(_: number, account: GroupedAccount): string {
    return account.accountId;
  }

  private subscribeToContactsState() {
    this.contactsFilteredBySearch$ = combineLatest([
      this.searchInputControl.valueChanges.pipe(
        startWith(''),
        map((query: string) => query.toLowerCase()),
      ),
      this.contacts$,
    ]).pipe(map(([query, contacts]) => this.filterContacts(contacts, query)));

    this.groupedAccounts$ = combineLatest([
      this.contacts$,
      this.searchInputControl.valueChanges.pipe(
        startWith(''),
        map((query: string) => query.toLowerCase()),
      ),
      this.activeAccount$,
    ]).pipe(
      switchMap(([contacts, query, currentAccount]) => {
        contacts = this.filterContacts(contacts, query);

        const groupedAccounts: GroupedAccount[] = [];

        if (currentAccount && contacts.length > 0) {
          groupedAccounts.push({
            ...currentAccount,
            name: `${currentAccount.externalId} - ${currentAccount.name}`,
            users: [],
            active: true,
          });
        }

        contacts.forEach((user) => {
          user.accounts.forEach((account) => {
            let index = groupedAccounts.findIndex(
              (value) => value.externalId === account.externalId,
            );
            if (index === -1) {
              index =
                groupedAccounts.push({
                  ...account,
                  name: `${account.externalId} - ${account.name}`,
                  users: [],
                  active: false,
                }) - 1;
            }

            groupedAccounts[index].users.push(user);
          });
        });
        return of(groupedAccounts);
      }),
    );
  }

  private filterContacts(contacts: Contact[], query: string) {
    return contacts?.filter(({ displayName, email, accounts }: Contact) => {
      const searchableData = {
        displayName: this.normalize(displayName),
        email: this.normalize(email),
        accounts,
      };
      const searchableString = JSON.stringify(searchableData);
      return searchableString.includes(query.trim());
    });
  }

  private normalize(str: string): string {
    return str.trim().toLowerCase().normalize();
  }
}
